// Hand-painted maps, made in the editor and kept in the browser.
//
// A map is the same two-dimensional array of squares the generators produce,
// with two more layers over it — what each square is floored with, and which
// squares carry a shop front beside their door — and two markers: where the
// party starts, and where the way down lies. Towns are what these are for at
// the moment, so a map needs both markers before it can be played.

import { TILE } from './dungeon';
import { buildTown } from './town';
import { TOWN_THEME } from './textures';

const STORE_KEY = 'frpg.maps';
export const MAX_MAPS = 12;
export const MAX_NAME = 24;

export const MIN_SIZE = 8;
export const MAX_SIZE = 40;
export const DEFAULT_SIZE = 14;

const WALL = 1;

// The ways down out of a town, each with its own painted archway
export const ENTRANCES = {
    stairs: { name: 'Stairway', panel: 'stairs_up_wall' },
    sewer: { name: 'Sewer', panel: 'entrance_sewer' },
    tower: { name: 'Ruined tower', panel: 'entrance_tower' },
    castle: { name: 'Castle gate', panel: 'entrance_castle' },
};

export const DEFAULT_ENTRANCE = 'stairs';

// ---------------------------------------------------------------- the map

function grid(size, fill) {
    return Array.from({ length: size }, () => new Array(size).fill(fill));
}

// Solid rock, walled in: somewhere to start carving
export function blankMap(size = DEFAULT_SIZE, name = '') {
    return {
        name,
        size,
        tiles: grid(size, WALL),
        floors: grid(size, null),
        surrounds: grid(size, null),
        start: null,
        entrance: null,
        entranceStyle: DEFAULT_ENTRANCE,
    };
}

// A generated town to paint over, rather than starting from bare rock
export function randomMap(size = DEFAULT_SIZE, name = '') {
    const town = buildTown(size);
    const map = blankMap(size, name);
    map.tiles = town.tiles;
    map.surrounds = town.surrounds;
    map.floors = town.floors;
    map.entranceStyle = town.entranceStyle || DEFAULT_ENTRANCE;
    map.start = town.start_position;
    map.entrance = town.gate;
    map.tiles[town.gate[1]][town.gate[0]] = TILE.stairsUp;
    return map;
}

// Kept here rather than imported, so the editor and the dungeon agree even
// though the dungeon's own table is keyed the other way round
const FACILITY_TILE = {
    temple: TILE.temple,
    magic: TILE.magicSchool,
    market: TILE.market,
    training: TILE.trainer,
    tavern: TILE.tavern,
};

const within = (map, [x, y]) => x >= 0 && y >= 0 && x < map.size && y < map.size;

export const isBuilding = (tile) => tile === 1 || tile === 2;
export const isDoorway = (tile) => tile >= 3 && tile <= 6;

// Why this brush will not take here, or null if it will. The layers are not
// interchangeable: ground is laid on open squares, and a shop front hangs on
// a building or on the door cut into it.
export function paintBlocker(map, at, what) {
    if (!within(map, at)) return 'That is off the edge of the map.';
    const tile = map.tiles[at[1]][at[0]];
    if (what.floor !== undefined && isBuilding(tile)) {
        return 'Ground is laid on open squares, not inside a building.';
    }
    if (what.surround !== undefined && !isBuilding(tile) && !isDoorway(tile)) {
        return 'A shop front hangs on a building or on its door.';
    }
    return null;
}

// Paint one square. The start and the way down are markers rather than
// squares, so painting one moves it instead of leaving two behind.
export function paint(map, at, what) {
    if (paintBlocker(map, at, what)) return map;
    const [x, y] = at;
    const next = {
        ...map,
        tiles: map.tiles.map((row) => [...row]),
        floors: map.floors.map((row) => [...row]),
        surrounds: (map.surrounds || grid(map.size, null)).map((row) => [...row]),
    };

    if (what.marker === 'start') {
        // The party cannot start inside a wall
        if (next.tiles[y][x] === WALL || next.tiles[y][x] === 2) next.tiles[y][x] = TILE.floor;
        next.start = [x, y];
        return next;
    }
    if (what.marker === 'entrance') {
        next.tiles[y][x] = TILE.stairsUp;
        next.entrance = [x, y];
        return next;
    }
    if (what.floor !== undefined) {
        next.floors[y][x] = what.floor;
        return next;
    }
    if (what.surround !== undefined) {
        next.surrounds[y][x] = what.surround;
        return next;
    }

    next.tiles[y][x] = what.tile;
    // The layers follow the square: nothing grows under a building, and a
    // front has nothing to hang on once the wall it was on is gone
    if (isBuilding(what.tile)) next.floors[y][x] = null;
    if (!isBuilding(what.tile) && !isDoorway(what.tile)) next.surrounds[y][x] = null;
    // A marker whose square has been built over is no longer there
    if (next.start && next.start[0] === x && next.start[1] === y && !isWalkable(what.tile)) next.start = null;
    if (next.entrance && next.entrance[0] === x && next.entrance[1] === y && what.tile !== TILE.stairsUp) {
        next.entrance = null;
    }
    return next;
}

export function isWalkable(tile) {
    return tile !== 1 && tile !== 2 && tile !== 3 && tile !== 5;
}

// Grow or crop a map, keeping the top-left corner where it is
export function resize(map, size) {
    const bounded = Math.max(MIN_SIZE, Math.min(MAX_SIZE, size));
    const next = blankMap(bounded, map.name);
    next.entranceStyle = map.entranceStyle;
    for (let y = 0; y < Math.min(bounded, map.size); y++) {
        for (let x = 0; x < Math.min(bounded, map.size); x++) {
            next.tiles[y][x] = map.tiles[y][x];
            next.floors[y][x] = map.floors[y][x];
            next.surrounds[y][x] = (map.surrounds && map.surrounds[y][x]) || null;
        }
    }
    const kept = (at) => (at && within(next, at) ? at : null);
    next.start = kept(map.start);
    next.entrance = kept(map.entrance);
    return next;
}

// Squares reachable on foot from the party's starting square
function reachable(map) {
    const seen = grid(map.size, false);
    if (!map.start || !isWalkable(map.tiles[map.start[1]][map.start[0]])) return seen;
    const queue = [map.start];
    seen[map.start[1]][map.start[0]] = true;
    while (queue.length) {
        const [x, y] = queue.shift();
        [[0, -1], [1, 0], [0, 1], [-1, 0]].forEach(([dx, dy]) => {
            const next = [x + dx, y + dy];
            if (!within(map, next) || seen[next[1]][next[0]]) return;
            if (!isWalkable(map.tiles[next[1]][next[0]])) return;
            seen[next[1]][next[0]] = true;
            queue.push(next);
        });
    }
    return seen;
}

// What still stands between this map and being playable, or null if nothing does
export function mapBlocker(map) {
    if (!map.name || !map.name.trim()) return 'The map needs a name.';
    if (!map.start) return 'Mark where the party starts.';
    if (!map.entrance) return 'Mark the way down into the dungeon.';
    const seen = reachable(map);
    if (!seen[map.entrance[1]][map.entrance[0]]) {
        return 'There is no way to walk from the start to the dungeon entrance.';
    }
    return null;
}

// What the map holds, for the editor's status line
export function describeMap(map) {
    const counts = {};
    map.tiles.forEach((row) => row.forEach((tile) => { counts[tile] = (counts[tile] || 0) + 1; }));
    const open = Object.entries(counts)
        .filter(([tile]) => isWalkable(Number(tile)))
        .reduce((sum, [, n]) => sum + n, 0);
    const trades = Object.values(FACILITY_TILE).filter((tile) => counts[tile]).length;
    return `${map.size}×${map.size} · ${open} squares open · ${trades} of 5 trades`;
}

// ---------------------------------------------------------------- playing

// Doors painted on a map, as the game's record of them. A door with walls
// left and right is crossed north to south, and the other way round.
function doorStatesFor(tiles) {
    const states = {};
    const wall = (x, y) => tiles[y] && (tiles[y][x] === 1 || tiles[y][x] === 2);
    tiles.forEach((row, y) => row.forEach((tile, x) => {
        if (tile < 3 || tile > 6) return;
        const sideways = wall(x - 1, y) && wall(x + 1, y);
        states[`${x},${y}`] = {
            isOpen: tile === 4 || tile === 6,
            orientation: sideways ? 'vertical' : 'horizontal',
            locked: false,
            maxHp: 10,
            hp: 10,
            endurance: 1,
        };
    }));
    return states;
}

// A painted map, as a level the game can drop the party into
export function levelFromMap(map, level = 1) {
    const tiles = map.tiles.map((row) => [...row]);
    return {
        level,
        town: true,
        name: map.name,
        painted: true,
        theme: TOWN_THEME,
        tiles,
        floors: map.floors.map((row) => [...row]),
        surrounds: (map.surrounds || grid(map.size, null)).map((row) => [...row]),
        // A painted map's doors are shut but unlocked, like a town's
        doorStates: doorStatesFor(tiles),
        entranceStyle: map.entranceStyle,
        start: map.start,
        gate: map.entrance,
        keyChestLocation: null,
        monsters: [],
        explored: tiles.map((row) => row.map(() => true)),
        hasKey: false,
        stairs: {},
    };
}

// ---------------------------------------------------------------- storage

function read() {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        const maps = raw ? JSON.parse(raw) : [];
        return Array.isArray(maps) ? maps : [];
    } catch (err) {
        // Unreadable or blocked storage: carry on with no maps
        return [];
    }
}

function write(maps) {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(maps));
        return true;
    } catch (err) {
        console.warn('Could not save the map', err);
        return false;
    }
}

export function tidyName(name) {
    return String(name || '').trim().slice(0, MAX_NAME);
}

export function listMaps() {
    return read().sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

export function findMap(name) {
    const wanted = tidyName(name).toLowerCase();
    return listMaps().find((map) => map.name.toLowerCase() === wanted) || null;
}

export function saveMap(map) {
    const name = tidyName(map.name);
    if (!name) return null;
    const saved = { ...map, name, savedAt: Date.now() };
    const others = read().filter((existing) => existing.name.toLowerCase() !== name.toLowerCase());
    return write([saved, ...others].slice(0, MAX_MAPS)) ? saved : null;
}

export function deleteMap(name) {
    const wanted = tidyName(name).toLowerCase();
    return write(read().filter((map) => map.name.toLowerCase() !== wanted));
}

// Maps travel between people as plain JSON
export function toJson(map) {
    const { name, size, tiles, floors, surrounds, start, entrance, entranceStyle } = map;
    return JSON.stringify({ name, size, tiles, floors, surrounds, start, entrance, entranceStyle }, null, 1);
}

export function fromJson(text) {
    const raw = JSON.parse(text);
    const size = Math.max(MIN_SIZE, Math.min(MAX_SIZE, Number(raw.size) || DEFAULT_SIZE));
    const map = blankMap(size, tidyName(raw.name));
    if (!Array.isArray(raw.tiles)) throw new Error('That file has no squares in it.');
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const tile = raw.tiles[y] && raw.tiles[y][x];
            map.tiles[y][x] = Number.isInteger(tile) ? tile : WALL;
            const floor = raw.floors && raw.floors[y] && raw.floors[y][x];
            map.floors[y][x] = typeof floor === 'string' ? floor : null;
            const front = raw.surrounds && raw.surrounds[y] && raw.surrounds[y][x];
            map.surrounds[y][x] = typeof front === 'string' ? front : null;
        }
    }
    const marker = (at) => (Array.isArray(at) && at.length === 2 && at.every((n) => n >= 0 && n < size) ? [at[0], at[1]] : null);
    map.start = marker(raw.start);
    map.entrance = marker(raw.entrance);
    map.entranceStyle = ENTRANCES[raw.entranceStyle] ? raw.entranceStyle : DEFAULT_ENTRANCE;
    return map;
}
